import { Action, any, async, fromJSX, loadingAsyncState, nothing } from "widgets-for-react";
import { fetchEvents, GroupedOverviewResult } from "../../../backendApi/EventsApi";
import { GroupedOverviewFilterOptions } from "../../../backendApi/generated";
import { previousOrDefault, previousOrNull } from "../../../utils/asyncState";
import { emptyJumbo, JumboProps } from "../../Jumbo/Jumbo";
import { EventOverview } from "./EventOverviewLayout/EventOverview";
import { emptyFilterOptions, emptyFilters, EventOverviewState, EventsOverviewClassNames, FilterSettings } from "./EventOverviewState";
import debounce = require("lodash.debounce")

const defaultFrom = emptyFilters.from

export const loadEvents = (filterSettings: FilterSettings, events: EventOverviewState['events']) =>
  loadingAsyncState(fetchEvents(filterSettings), previousOrNull(events))

const reloadEvents = (setState: (_: Action<EventOverviewState>) => void) =>
  setState((s0) => ({
    ...s0,
    events: loadingAsyncState(fetchEvents({ ...s0.filterSettings, from: defaultFrom }), previousOrNull(s0.events))
  }))

const debouncedReloadEvents = debounce(reloadEvents, 200)


export const EventOverviewWidget = (props: EventOverviewState & { eventsOverviewClassNames: EventsOverviewClassNames }) =>
  any<Action<EventOverviewState>>({ key: 'EventOverview' })(
    [
      // Render Layout
      fromJSX((setState) => {
        const applyAndLoad = (newSettings: Partial<FilterSettings>) => setState(s0 => {
          const filterSettings: FilterSettings = { ...s0.filterSettings, ...newSettings }
          const events = loadEvents(filterSettings, s0.events)
          return { ...s0, filterSettings, events }
        })

        return EventOverview({
          ...props,
          jumboProps: previousOrDefault(props.jumboContent, emptyJumbo),
          filterOptions: previousOrDefault(props.filterOptions, emptyFilterOptions),
          filterUpdaters: {
            updatePage: (page) => applyAndLoad({ page, from: null }),
            updateTags: (tags) => applyAndLoad({ tags, page: 1 }),
            updateRegions: (regions) => applyAndLoad({ regions, page: 1 }),
            updateLocations: (locations) => applyAndLoad({ locations, page: 1 }),
            updateQuery: (query) => {
              setState(s0 => ({ ...s0, filterSettings: { ...s0.filterSettings, query } }))
              debouncedReloadEvents(setState)
            },
            // updateFrom: (from: Date) => applyAndLoad({ from, page: undefined}),
            updateFrom: (from: Date) => {
              setState(s0 => {
                const filterSettings: FilterSettings = { ...s0.filterSettings, from, page: undefined }

                const events = loadEvents(filterSettings, s0.events)
                return { ...s0, filterSettings, events }
              })
            },
            toggleFilterVisibility: () => setState(s0 => ({ ...s0, filterExpanded: !s0.filterExpanded })),
            resetFilter: () => applyAndLoad(emptyFilters)
          },
        })
      }),

      // Manage async state updates
      async<GroupedOverviewResult>(nothing, { key: 'eventsLoader' })(props.events)
        .map(resultUpdater => s0 => {
          const events = resultUpdater(s0.events)
          if (!s0.filterExpanded && events.kind == 'loaded') {
            const element = document.getElementById("activity_results")
            element?.scrollIntoView({ behavior: "smooth" })
          }
          return ({ ...s0, events })
        }),
      async<JumboProps>(nothing, { key: 'jumboLoader' })(props.jumboContent)
        .map(resultUpdater => s0 => {
          const jumboContent = resultUpdater(s0.jumboContent)
          return ({ ...s0, jumboContent })
        }),
      async<GroupedOverviewFilterOptions>(nothing, { key: 'filtersLoader' })(props.filterOptions)
        .map(resultUpdater => s0 => {
          const filterOptions = resultUpdater(s0.filterOptions)
          return ({ ...s0, filterOptions })
        })
    ]
  ).map(a => a(props))
